import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { EditIcon, PlusIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { WorkingHoursTable } from '@/components/common/WorkingHoursTable';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { Employee, WorkingTimeModel } from '../data/databaseSchema';
import { type EmployeeWorkingModelsDetail, employeeService } from '../services/employeeService';
import { workingTimeModelService } from '../services/workingTimeModelService';

const CREATE_NEW_MODEL_VALUE = '__create_new_model__';

interface EmployeeWorkingModelFormDrawerProps {
  employee: Employee | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  entry?: EmployeeWorkingModelsDetail | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
  isDeleteDisabled?: boolean;
}

export const EmployeeWorkingModelFormDrawer: React.FC<EmployeeWorkingModelFormDrawerProps> = ({
  employee,
  isOpen,
  onClose,
  onSave,
  entry,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
  isDeleteDisabled = false,
}) => {
  const [formData, setFormData] = useState({
    working_time_model_id: '',
    start_date: '',
    end_date: '',
    no_end_date: false,
    status: 'active',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCreateNewModel = () => {
    onClose();
    navigate({ to: '/settings/working-time-model' });
  };

  const {
    data: workingTimeModelsData,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useQuery({
    queryKey: ['workingTimeModels'],
    queryFn: () => workingTimeModelService.getWorkingTimeModels(),
    enabled: isOpen,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const selectedModel = useMemo(() => {
    if (!workingTimeModelsData?.length) return null;
    return workingTimeModelsData.find(
      (m: WorkingTimeModel) => m.id.toString() === formData.working_time_model_id
    );
  }, [workingTimeModelsData, formData.working_time_model_id]);

  const selectedModelLabel = useMemo(() => {
    if (selectedModel) {
      return `Model ${selectedModel.name} - ${selectedModel.hours}h/week`;
    }
    if (formData.working_time_model_id && (isEdit || readOnly) && entry?.name) {
      return `Model ${entry.name} - ${entry.hours}h/week`;
    }
    return undefined;
  }, [selectedModel, formData.working_time_model_id, isEdit, readOnly, entry]);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && entry) {
        const startDate = entry.start_date ? new Date(entry.start_date) : null;
        const endDate = entry.end_date ? new Date(entry.end_date) : null;
        setFormData({
          working_time_model_id: entry.working_time_model_id?.toString() || '',
          start_date: startDate ? startDate.toISOString().split('T')[0] : '',
          end_date: endDate ? endDate.toISOString().split('T')[0] : '',
          no_end_date: entry.no_end_date || false,
          status: entry.status || 'active',
        });
      } else {
        setFormData({
          working_time_model_id: '',
          start_date: '',
          end_date: '',
          no_end_date: false,
          status: 'active',
        });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, entry]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.working_time_model_id) {
      newErrors.working_time_model_id = 'Working Time Model is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start Date is required';
    }
    if (!formData.no_end_date) {
      if (!formData.end_date) {
        newErrors.end_date = 'End Date is required';
      }
      if (
        formData.start_date &&
        formData.end_date &&
        new Date(formData.start_date) > new Date(formData.end_date)
      ) {
        newErrors.end_date = 'End Date must be after Start Date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !employee?.employee_id) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const apiData: {
        working_time_model_id: string;
        start_date: string;
        end_date?: string;
        no_end_date?: boolean;
        status?: string;
      } = {
        working_time_model_id: formData.working_time_model_id,
        start_date: new Date(formData.start_date).toISOString(),
        status: formData.status,
        no_end_date: formData.no_end_date,
      };

      if (!formData.no_end_date && formData.end_date) {
        apiData.end_date = new Date(formData.end_date).toISOString();
      }

      if (isEdit && entry) {
        await employeeService.updateEmployeeWorkingModel(employee.employee_id, entry.id, apiData);
      } else {
        await employeeService.createEmployeeWorkingModel(employee.employee_id, apiData);
      }

      onSave();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modelSelectPlaceholder = isLoadingModels
    ? 'Loading models...'
    : modelsError
      ? 'Failed to load models'
      : 'Select a working time model';

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Time Model' : isEdit ? 'Edit Time Model' : 'Add Time Model'}
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update Model' : 'Add Model'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={isDeleteDisabled}
              onClick={onDeleteRequest}
            >
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <Field data-invalid={!!errors.working_time_model_id}>
          <FieldLabel>Working Time Model *</FieldLabel>
          <Select
            value={formData.working_time_model_id}
            onValueChange={(value) => {
              if (value == null) return;
              if (value === CREATE_NEW_MODEL_VALUE) {
                handleCreateNewModel();
                return;
              }
              setFormData((prev) => ({ ...prev, working_time_model_id: value }));
              if (errors.working_time_model_id) {
                setErrors((prev) => ({ ...prev, working_time_model_id: '' }));
              }
            }}
            disabled={fieldsDisabled || isLoadingModels || !!modelsError}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={modelSelectPlaceholder}>{selectedModelLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[500px]">
              {workingTimeModelsData?.map((model: WorkingTimeModel) => (
                <SelectItem key={model.id} value={model.id.toString()} className="py-3">
                  <div className="flex w-full flex-col gap-2">
                    <span>
                      Model {model.name} - {model.hours}h/week
                    </span>
                    <WorkingHoursTable model={model} className="text-[9px]" />
                  </div>
                </SelectItem>
              ))}
              {!readOnly ? (
                <>
                  <SelectSeparator />
                  <SelectItem value={CREATE_NEW_MODEL_VALUE} className="text-link">
                    <PlusIcon className="size-4" />
                    Create new model
                  </SelectItem>
                </>
              ) : null}
            </SelectContent>
          </Select>
          {errors.working_time_model_id ? (
            <FieldError>{errors.working_time_model_id}</FieldError>
          ) : null}
          {selectedModel ? (
            <div className="mt-3">
              <WorkingHoursTable model={selectedModel} className="text-[9px]" />
            </div>
          ) : null}
        </Field>
        <TextFormField
          label="Start Date"
          type="date"
          required
          value={formData.start_date}
          onChange={(value) => {
            setFormData((prev) => ({ ...prev, start_date: value }));
            if (errors.start_date) {
              setErrors((prev) => ({ ...prev, start_date: '' }));
            }
          }}
          error={errors.start_date}
          disabled={fieldsDisabled}
        />
        <Field>
          <div className="flex items-center gap-3">
            <Switch
              id="working-no-end-date"
              checked={formData.no_end_date}
              disabled={fieldsDisabled}
              onCheckedChange={(checked) => {
                const noEndDate = checked === true;
                setFormData((prev) => ({
                  ...prev,
                  no_end_date: noEndDate,
                  end_date: noEndDate ? '' : prev.end_date,
                }));
                if (errors.end_date) {
                  setErrors((prev) => ({ ...prev, end_date: '' }));
                }
              }}
            />
            <Label htmlFor="working-no-end-date">No End Date</Label>
          </div>
        </Field>
        {!formData.no_end_date ? (
          <TextFormField
            label="End Date"
            type="date"
            required
            value={formData.end_date}
            onChange={(value) => {
              setFormData((prev) => ({ ...prev, end_date: value }));
              if (errors.end_date) {
                setErrors((prev) => ({ ...prev, end_date: '' }));
              }
            }}
            error={errors.end_date}
            disabled={fieldsDisabled}
          />
        ) : null}
        <SelectFormField
          label="Status"
          value={formData.status}
          onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
          disabled={fieldsDisabled}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
